import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, MenuItem, CircularProgress, alpha, useTheme,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useCheckBulkStaffAvailabilityMutation, useBulkSwitchClassStaffMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";

/**
 * Bulk-switch staff across many selected occurrences. A candidate is validated
 * against every selected session before confirmation; the operation is all-or-
 * nothing (one conflict blocks all) and the backend re-validates on submit.
 */
const BulkSwitchStaffDialog: React.FC<{
  open: boolean;
  rows: any[];
  staffList: any[];
  onClose: () => void;
  onSuccess?: () => void;
}> = ({ open, rows, staffList, onClose, onSuccess }) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();
  const [checkBulk] = useCheckBulkStaffAvailabilityMutation();
  const [bulkSwitch, { isLoading }] = useBulkSwitchClassStaffMutation();

  const [newStaffId, setNewStaffId] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{ available: boolean; checkedCount: number; conflicts: Array<{ label: string }> } | null>(null);

  const occurrenceIds = rows.map((r) => r.id);

  useEffect(() => {
    if (open) {
      setNewStaffId("");
      setResult(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !newStaffId) {
      setResult(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    (async () => {
      try {
        const res = await checkBulk({ occurrenceIds, newStaffId: Number(newStaffId) }).unwrap();
        if (cancelled) return;
        if (res.status) {
          const d: any = res.data || {};
          setResult({ available: d.available, checkedCount: d.checkedCount ?? occurrenceIds.length, conflicts: d.conflicts || [] });
        } else {
          setResult(null);
          showErrorMessage(res.message);
        }
      } catch {
        if (!cancelled) setResult(null);
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, newStaffId]);

  const submit = async () => {
    try {
      const res = await bulkSwitch({ occurrenceIds, newStaffId: Number(newStaffId) }).unwrap();
      if (res.status) {
        showMessage(res.message);
        onSuccess?.();
        onClose();
      } else showErrorMessage(res.message || "Failed to switch staff");
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to switch staff");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Switch Staff — {rows.length} session(s)</DialogTitle>
      <DialogContent>
        <TextField select label="New Staff *" size="small" fullWidth value={newStaffId} onChange={(e) => setNewStaffId(e.target.value)} sx={{ mb: 2 }}>
          <MenuItem value="">Select staff...</MenuItem>
          {staffList.map((s: any) => <MenuItem key={s.id} value={String(s.id)}>{s.teamFirstName} {s.teamLastName}</MenuItem>)}
        </TextField>

        {newStaffId && (
          <Box>
            {checking ? (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={14} /> Validating {occurrenceIds.length} selected session(s)…
              </Typography>
            ) : result ? (
              result.available ? (
                <Typography sx={{ fontSize: 13, color: "success.main", fontWeight: 700 }}>
                  Available for all {result.checkedCount} selected session{result.checkedCount === 1 ? "" : "s"}
                </Typography>
              ) : (
                <Box sx={{ p: 1.5, borderRadius: "8px", bgcolor: alpha(theme.palette.error.main, 0.06), border: `1px solid ${alpha(theme.palette.error.main, 0.25)}` }}>
                  <Typography sx={{ fontSize: 13, color: "error.main", fontWeight: 700, mb: 0.5 }}>
                    Unavailable for {result.conflicts.length} selected session{result.conflicts.length === 1 ? "" : "s"}
                  </Typography>
                  {result.conflicts.slice(0, 10).map((c, i) => <Typography key={i} sx={{ fontSize: 11.5, color: "text.secondary" }}>• {c.label}</Typography>)}
                  {result.conflicts.length > 10 && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>…and {result.conflicts.length - 10} more</Typography>}
                </Box>
              )
            ) : null}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!newStaffId || isLoading || checking || (result ? !result.available : true)}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <SwapHorizIcon />}>
          {isLoading ? "Switching..." : `Switch ${rows.length} session(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkSwitchStaffDialog;
